/**
 * Helpers for the pass-collection screen.
 *
 * The server decides what counts as a pass — it reads the MEMBERSHIP and DAILY_PASS bits off the
 * product catalogue — and sends back each order carrying only its pass lines. Nothing here
 * re-derives that; these only slice the one response the filters work over.
 */

export const PENDING = 0;
export const COLLECTED = 1;
export const NEVER_COLLECTED = 2;

export const STATUSES = [
  { id: PENDING, label: "Pending" },
  { id: COLLECTED, label: "Collected" },
  { id: NEVER_COLLECTED, label: "Never collected" },
];

/** Rows written before the column was populated carry null, which reads as pending. */
export const collectionState = (order) =>
  order == null || order.passCollected == null ? PENDING : order.passCollected;

export const statusLabel = (state) => {
  const found = STATUSES.find((status) => status.id === state);
  return found != null ? found.label : "Pending";
};

export const passLines = (order) =>
  order != null && order.selectedProducts != null ? order.selectedProducts : [];

/** Four coupons is four things to hand over, not one line. */
export const passCount = (order) =>
  passLines(order).reduce((total, line) => total + (line.qty || 0), 0);

const matchesSearch = (order, term) => {
  if (term == null || term.trim() === "") return true;
  const needle = term.trim().toLowerCase();
  return ["userName", "userEmail", "userMobile", "orderRcptId"].some((field) => {
    const value = order[field];
    return value != null && String(value).toLowerCase().includes(needle);
  });
};

/** The visible rows. Status and search narrow independently. */
export const filterOrders = (orders, { status, search } = {}) =>
  (orders || []).filter(
    (order) =>
      (status == null || collectionState(order) === status) && matchesSearch(order, search)
  );

/**
 * Counts for the status filter.
 *
 * Runs through filterOrders with the status swapped out, so a badge can never promise rows the
 * table will not show. It used to be a separate pass that ignored the search box, which showed
 * "Pending 2" over an empty table.
 */
export const statusCounts = (orders, criteria = {}) =>
  STATUSES.reduce((counts, status) => {
    counts[status.id] = filterOrders(orders, { ...criteria, status: status.id }).length;
    return counts;
  }, {});
